import {
    Directive,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import {
    filter,
    finalize,
    fromEvent,
    map,
    merge,
    Observable,
    Subscription,
    switchMap,
    takeUntil,
    tap,
} from "rxjs";

import { isTwoFingersTouch } from "../gestures.helper";
import { PanEvent, Position } from "../gestures.types";

/**
 * @description
 *
 * A directive to track pan events on element (allowing translating an element via pan gesture).
 *
 * @usageNotes
 *
 * Set pan events to html element.
 *
 * ```
 * <some-element hPan (panStart)="onStartPan()" (panMove)="onPinchMove()" (panEnd)="onPanEnd()">
 *  ...
 * </some-element>
 * ```
 */
@Directive({
    selector: "[hPan]",
    standalone: true,
    host: {
        "[style.touch-action]": '"none"',
    },
})
export class PanDirective implements OnInit, OnDestroy {
    @Output() public panStart = new EventEmitter<PanEvent>();
    @Output() public panMove = new EventEmitter<PanEvent>();
    @Output() public panEnd = new EventEmitter<void>();

    private subscription = new Subscription();
    private startX = 0;
    private startY = 0;
    private lastDeltaX = 0;
    private lastDeltaY = 0;

    constructor(private element: ElementRef<HTMLElement>) {}

    public ngOnInit(): void {
        const element = this.element.nativeElement;

        // A pan gesture starts as soon as user triggers pointer down (mobile or desktop).
        const start$: Observable<Position> = fromEvent<PointerEvent>(
            element,
            "pointerdown",
        ).pipe(
            filter((event) => event.isPrimary),
            map((event) => {
                event.preventDefault();
                return { x: event.clientX, y: event.clientY };
            }),
        );

        // A pan gesture continues as soon as the user move the pointer.
        const move$ = fromEvent<PointerEvent>(document, "pointermove", {
            passive: false,
        }).pipe(filter((event) => event.isPrimary));

        // A pan gesture is canceled on mobile if user interact with two fingers.
        const twoFinger$ = fromEvent<TouchEvent>(element, "touchstart", {
            passive: false,
        }).pipe(
            filter(isTwoFingersTouch),
            tap((event) => event.preventDefault()),
        );

        // A pan gesture is generally canceled on pointer up, cancel or two fingers on mobile.
        const end$ = merge(
            fromEvent<PointerEvent>(document, "pointerup"),
            fromEvent<PointerEvent>(document, "pointercancel"),
            twoFinger$,
        ).pipe(
            filter(
                (event) => !(event instanceof PointerEvent) || event.isPrimary,
            ),
        );

        const pan$ = start$.pipe(
            tap((startPosition: Position) => {
                this.startX = startPosition.x;
                this.startY = startPosition.y;
                this.panStart.emit({
                    deltaX: 0,
                    deltaY: 0,
                    isFinal: false,
                });
            }),
            switchMap(() =>
                move$.pipe(
                    tap((event) => {
                        event.preventDefault();
                        this.lastDeltaX = event.clientX - this.startX;
                        this.lastDeltaY = event.clientY - this.startY;
                        const panEvent = {
                            deltaX: this.lastDeltaX,
                            deltaY: this.lastDeltaY,
                            isFinal: false,
                        };
                        this.panMove.emit(panEvent);
                    }),
                    takeUntil(end$),
                    finalize(() => {
                        // Emit last move event at end to get updated position.
                        this.panMove.emit({
                            deltaX: this.lastDeltaX,
                            deltaY: this.lastDeltaY,
                            isFinal: true,
                        });
                        this.panEnd.emit();
                    }),
                ),
            ),
        );

        this.subscription = pan$.subscribe();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
